import React, { FC, useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { useFormik } from 'formik';
import * as yup from 'yup';
import OtpInput from 'react-otp-input';
import FormikErrors from '../../../shared/FormikErrors/FormikErrors';
import { KTSVG } from '../../../../_metronic/helpers'
import { sendCode } from '../core/_requests'
import './TwoFactor.scss'
import Countdown from 'react-countdown'

export const TFA_EMAIL_PROVIDER = 'EmailCode';
export const TFA_PHONE_PROVIDER = 'PhoneCode';
export const TFA_GOOGLE_AUTHENTICATOR_PROVIDER = 'GoogleAuthenticator';

export interface TwoFactorProps {
    show: boolean;
    options: TwoFactorProviderOptions[];
    onClose: () => void;
    onSubmit: (code: string, provider: string) => void;
    onError: (error: any) => void;
}

export interface TwoFactorProviderOptions {
    name: string;
    value: string;
    iconPath: string;
    description: string;
}

const initialValues = {
    otp: ''
}

const validationSchema = yup.object().shape({
    otp: yup.string().trim()
        .min(6, 'Please enter a valid code.')
        .max(6, 'Please enter a valid code.')
        .required('Please enter a valid code.')
});

const TwoFactor: FC<TwoFactorProps> = (props) => {
    const [otpMethod, setOtpMethod] = useState<string | undefined>(undefined);
    const [hasConfirmedOtpMethod, setHasConfirmedOtpMethod] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [hasCompletedCountdown, setHasCompletedCountdown] = useState(false);
    const [countdownMs, setCountdownMs] = useState(0); 
    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            props.onSubmit(values.otp, otpMethod as string);
            resetState();
        },
    });

    useEffect(() => {
        const countdownInterval = window.setInterval(() => setCountdownMs(prev => prev - 1000), 1000);
        return () => clearInterval(countdownInterval);
    }, []);

    useEffect(() => {
        setCountdownMs(180000); // 3 mins
    }, [hasConfirmedOtpMethod]);

    useEffect(() => {
        resetState();
        if (!props.options || props.options.length <= 0) {
            setError('You have not set up your two-factor authentication.');
        }
        else if (props.options.length === 1) {
            // If there is only one available option, select it.
            setOtpMethod(props.options[0].value);
        }
    }, [props.options]);

    const resetState = () => {
        setError(undefined);
        setOtpMethod(undefined);
        setHasConfirmedOtpMethod(false);
        formik.resetForm();
    }

    const handleClose = () => {
        if (!formik.isSubmitting) {
            resetState();
            props.onClose();
        }
    }

    const handleSelectOtpMethod = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setOtpMethod(ev.target.value);
    }

    const handleConfirmOtpMethod = () => {
        if (otpMethod) {
            setLoading(true);
            sendCode(otpMethod).then(() => {
                setHasConfirmedOtpMethod(true);
            })
            .catch(err => props.onError(err))
            .finally(() => {
                setLoading(false);
            })
        }
    }

    const handleUnconfirmOtpMethod = () => {
        setHasConfirmedOtpMethod(false);
        formik.resetForm();
    }

    const countdownRenderer = ({hours, minutes, seconds, completed} : {hours: number, minutes: number, seconds: number, completed: boolean}) => {
        if(completed) {
            setHasCompletedCountdown(true);
        }

        return <span>{minutes}:{seconds}</span>
    }

    return <>
        <Modal show={props.show} onHide={handleClose} centered backdrop="static" scrollable size='lg' >
            <Modal.Header closeButton>
                <Modal.Title>Two-Factor Authentication Required</Modal.Title>
            </Modal.Header>
            <Modal.Body >


                {error && (
                    <div className="pt-10 pb-15 px-lg-17 text-danger fs-5 fw-semibold text-center">
                        {error}
                    </div>
                )}

                {!error && (!otpMethod || !hasConfirmedOtpMethod) && (
                    <div className="pt-10 pb-15 px-lg-17">

                        <p className="text-muted fs-5 fw-semibold mb-10" >
                            Choose the method that works best for you.
                        </p>

                        <div className="mb-10">
                            {
                                props.options.map(option =>
                                    <div key={option.value}>
                                        <label className="w-100">
                                            <input
                                                type="radio"
                                                className="btn-check"
                                                name="auth_option"
                                                value={option.value}
                                                id={`modal_two_factor_auth_option_${option.value}`}
                                                checked={otpMethod === option.value}
                                                onChange={handleSelectOtpMethod}
                                            />
                                            <div className="btn btn-outline btn-outline-dashed btn-active-light-primary p-7 d-flex align-items-center mb-5">
                                                <KTSVG
                                                    path={option.iconPath}
                                                    className='svg-icon svg-icon-4x me-4'
                                                />
                                                <span className="d-block fw-semibold text-start">
                                                    <span className="text-dark fw-bold d-block fs-3">{option.name}</span>
                                                    <span className="text-muted fw-semibold fs-6">{option.description}</span>
                                                </span>
                                            </div>
                                        </label>
                                    </div>
                                )
                            }
                        </div>

                        <div className="d-grid gap-2">
                            <Button type="button" onClick={handleConfirmOtpMethod} disabled={!otpMethod || hasCompletedCountdown}>
                                {!loading && (<span>Continue</span>)}
                                {loading && (<span>Sending...</span>)}
                            </Button>
                        </div>

                    </div>
                )}

                {!error && otpMethod && hasConfirmedOtpMethod && (
                    <Form noValidate onSubmit={formik.submitForm} id='reauth_form'>

                        {otpMethod === TFA_EMAIL_PROVIDER && (
                            <p className="text-muted fs-5 fw-semibold mb-10 text-center">
                                Enter the 6-digit verification code we've sent to your email.
                            </p>
                        )}

                        {otpMethod === TFA_GOOGLE_AUTHENTICATOR_PROVIDER && (
                            <p className="text-muted fs-5 fw-semibold mb-10 text-center">
                                Enter the 6-digit verification code from your authenticator app.
                            </p>
                        )}

                        <Form.Group className="mb-10">
                            <OtpInput
                                value={formik.values.otp}
                                onChange={(otp: any) => formik.setFieldValue('otp', otp)}
                                numInputs={6}
                                separator={<span className="w-15px"></span>}
                                containerStyle="justify-content-center"
                                inputStyle={`form-control otp-input ${formik.touched['otp'] && formik.errors['otp'] ? 'is-invalid' : ''}`}
                            />
                            {
                                formik.touched['otp'] && formik.errors['otp'] && <div className="mt-10 text-center">
                                    <FormikErrors formik={formik} fieldKey='otp' />
                                </div>
                            }
                        </Form.Group>

                        {otpMethod === TFA_EMAIL_PROVIDER && (
                            <p className="text-muted fs-5 fw-semibold mb-10 text-center">
                                <Countdown date={countdownMs} renderer={countdownRenderer} controlled>
                                    <div>
                                        Code expired. Please <a href="#" onClick={handleClose}>log in again</a>.
                                    </div>
                                </Countdown>
                            </p>
                        )}
                        
                        <div className="d-grid gap-5">
                            <Button type="button" onClick={formik.submitForm} disabled={formik.isSubmitting}>Continue</Button>
                        </div>

                    </Form>

                )}
            </Modal.Body>
        </Modal>
    </>;
}

export default TwoFactor;